/**
 * Path group card — slim header (selectable, opens conditions) + selectable
 * rule-path rows. Detail editing happens in the workspace right pane.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import type {
  VoluumPathGroupUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import GroupPathRow from './GroupPathRow';
import { getPathGroupDisplayLabel } from './path-group-display-label';
import {
  PathGroupHeader,
  RuleGroupOptimizationRow,
  SmartRotationRow,
} from './header';
import { getSetAutoOptimized } from '../utils/set-automation';
import { usePreviewMode, usePreviewRowLookup } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import { useTrackerAi } from '@/features/flow-optimizer/tracker';
import {
  PreviewSortLabel,
  usePreviewMetricSort,
} from '@/features/flow-optimizer/components/preview/sort';

export interface PathGroupCardProps {
  group: VoluumPathGroupUpdate;
  groupIndex: number;
  onPatch: (mutator: (g: VoluumPathGroupUpdate) => void) => void;
  isHeaderSelected: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isPathSelected: (pathIdx: number) => boolean;
  onSelectHeader: () => void;
  onSelectPath: (pathIdx: number) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const PathGroupCard: FC<PathGroupCardProps> = (props) => {
  const { group, isHeaderSelected, isExpanded } = props;
  const groupName = getPathGroupDisplayLabel(group);
  const paths = group.paths ?? [];

  return (
    <div
      className={cn(
        'rounded-md border border-border/70 bg-card shadow-none',
        !group.active && 'opacity-70',
        isHeaderSelected && 'ring-1 ring-primary/40',
      )}
    >
      <PathGroupHeader
        group={group}
        groupName={groupName}
        isHeaderSelected={isHeaderSelected}
        isExpanded={isExpanded}
        onToggleExpanded={props.onToggleExpanded}
        canMoveUp={props.canMoveUp}
        canMoveDown={props.canMoveDown}
        onSelectHeader={props.onSelectHeader}
        onPatch={props.onPatch}
        onMoveUp={props.onMoveUp}
        onMoveDown={props.onMoveDown}
        onDuplicate={props.onDuplicate}
        onDelete={props.onDelete}
      />

      {isExpanded && (
        <PathGroupBody
          group={group}
          groupName={groupName}
          paths={paths}
          isPathSelected={props.isPathSelected}
          onPatch={props.onPatch}
          onSelectPath={props.onSelectPath}
        />
      )}
    </div>
  );
};

interface PathGroupBodyProps {
  group: VoluumPathGroupUpdate;
  groupName: string;
  paths: VoluumPathUpdate[];
  isPathSelected: (pathIdx: number) => boolean;
  onPatch: (mutator: (g: VoluumPathGroupUpdate) => void) => void;
  onSelectPath: (pathIdx: number) => void;
}

const PathGroupBody: FC<PathGroupBodyProps> = ({
  group,
  groupName,
  paths,
  isPathSelected,
  onPatch,
  onSelectPath,
}) => {
  const previewMode = usePreviewMode();
  const lookup = usePreviewRowLookup();
  const { sort, cycle, sortEntries } = usePreviewMetricSort();
  const trackerAi = useTrackerAi();
  const weightLocked = trackerAi && getSetAutoOptimized(group);
  const pathEntries = useMemo(() => {
    const base = paths.map((path, index) => ({ path, index }));
    if (!previewMode) return base;
    return sortEntries(base, ({ path }) => ({
      active: path.active !== false,
      name: path.name ?? '',
      row: lookup('paths', path.id ?? ''),
    }));
  }, [paths, previewMode, sortEntries, lookup]);
  const patchPath = (idx: number, mutator: (p: VoluumPathUpdate) => void) =>
    onPatch((g) => {
      const target = g.paths?.[idx];
      if (target) mutator(target);
    });

  return (
    <div className="border-t border-border/40">
      <RuleGroupOptimizationRow
        group={group}
        groupName={groupName}
        onPatch={onPatch}
      />
      <SmartRotationRow
        checked={Boolean(group.smartRotation)}
        onChange={(next) =>
          onPatch((g) => {
            g.smartRotation = next;
          })
        }
        ariaLabel={`Smart rotation for ${groupName}`}
      />
      {paths.length > 0 && previewMode && (
        <div className="grid w-full items-center gap-x-1.5 border-t border-border/40 px-2 py-0.5 grid-cols-[minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]">
          <span />
          <PreviewSortLabel column="current" label="Current" sort={sort} onCycle={cycle} />
          <span />
          <PreviewSortLabel column="new" label="New" sort={sort} onCycle={cycle} />
          <PreviewSortLabel column="diff" label="Diff" sort={sort} onCycle={cycle} />
          <span />
        </div>
      )}
      {paths.length > 0 && (
        <ul
          role="listbox"
          aria-label={`Paths in ${groupName}`}
          className="divide-y divide-border/40 border-t border-border/40"
        >
          {pathEntries.map(({ path: p, index: idx }) => (
            <GroupPathRow
              key={p.id ?? `gp-${idx}`}
              path={p}
              selected={isPathSelected(idx)}
              weightLocked={weightLocked}
              onSelect={() => onSelectPath(idx)}
              onPatch={(m) => patchPath(idx, m)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default PathGroupCard;
